import type { ApiResponse } from "@/types/common";
import { apiClient, unwrapApiResponse } from "./httpClient";

export interface ChannelCatalogueItem {
  productId: number;
  name: string;
  sku?: string;
  barcode?: string;
  imageUrl?: string;
  categoryId?: number;
  categoryName?: string;
  description?: string;
  shortDescription?: string;
  price: number;
  customerPrice: number;
  mrp: number;
  distributorPrice?: number;
  wholesalePrice?: number;
  retailPrice?: number;
  availableQuantity: number;
  sellerCommissionPercent: number;
  onwardCommissionPercent: number;
  businessVolume: number;
  unit: string;
  unitValue: number;
  status: string;
  isActive: boolean;
}

export interface ChannelOrder {
  id: number;
  productId: number;
  productName: string;
  productSku?: string;
  productImageUrl?: string;
  buyerPartyId: number;
  buyerName: string;
  buyerPhone?: string;
  buyerLevel?: string;
  sellerPartyId?: number;
  sellerName: string;
  sellerPhone?: string;
  sellerLevel?: string;
  quantity: number;
  unitPrice: number;
  totalAmount?: number;
  commissionPercent: number;
  commissionAmount: number;
  businessVolume: number;
  paymentMethod: number;
  paymentMethodText: string;
  paymentStatus: number;
  paymentStatusText: string;
  paymentReference?: string;
  status: number;
  statusText: string;
  note?: string;
  rejectionReason?: string;
  createdAt: string;
  approvedAt?: string;
}

export interface ChannelOrderFilterParams {
  search?: string;
  status?: string;
  fromDate?: string;
  toDate?: string;
}

export interface ChannelCommissionSummary {
  balance: number;
  entries: {
    id: number;
    channelOrderId: number | null;
    productName: string;
    amount: number;
    balanceAfter: number;
    description: string;
    createdAt: string;
  }[];
}

export const channelOrdersService = {
  catalogue(params?: { search?: string; category?: string }): Promise<ChannelCatalogueItem[]> {
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.category && params.category !== "all") query.set("category", params.category);
    const qs = query.toString();
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelCatalogueItem[]>>(`/ChannelOrders/catalogue${qs ? `?${qs}` : ""}`));
  },

  getCatalogueItem(productId: number): Promise<ChannelCatalogueItem> {
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelCatalogueItem>>(`/ChannelOrders/catalogue/${productId}`));
  },

  create(dto: {
    productId: number;
    quantity: number;
    paymentMethod: number;
    paymentReference?: string;
    note?: string;
  }): Promise<ChannelOrder> {
    return unwrapApiResponse(apiClient.post<ApiResponse<ChannelOrder>>("/ChannelOrders", dto));
  },

  outgoing(params?: ChannelOrderFilterParams): Promise<ChannelOrder[]> {
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.status && params.status !== "all") query.set("status", params.status);
    if (params?.fromDate) query.set("fromDate", params.fromDate);
    if (params?.toDate) query.set("toDate", params.toDate);
    const qs = query.toString();
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelOrder[]>>(`/ChannelOrders/outgoing${qs ? `?${qs}` : ""}`));
  },

  incoming(params?: ChannelOrderFilterParams): Promise<ChannelOrder[]> {
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.status && params.status !== "all") query.set("status", params.status);
    if (params?.fromDate) query.set("fromDate", params.fromDate);
    if (params?.toDate) query.set("toDate", params.toDate);
    const qs = query.toString();
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelOrder[]>>(`/ChannelOrders/incoming${qs ? `?${qs}` : ""}`));
  },

  approve(id: number): Promise<ChannelOrder> {
    return unwrapApiResponse(apiClient.post<ApiResponse<ChannelOrder>>(`/ChannelOrders/${id}/approve`, {}));
  },

  reject(id: number, reason: string): Promise<ChannelOrder> {
    return unwrapApiResponse(apiClient.post<ApiResponse<ChannelOrder>>(`/ChannelOrders/${id}/reject`, { reason }));
  },

  cancel(id: number): Promise<ChannelOrder> {
    return unwrapApiResponse(apiClient.post<ApiResponse<ChannelOrder>>(`/ChannelOrders/${id}/cancel`));
  },

  commission(): Promise<ChannelCommissionSummary> {
    return unwrapApiResponse(apiClient.get<ApiResponse<ChannelCommissionSummary>>("/ChannelOrders/commission"));
  },
};
